import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import useAuth from 'hooks/useAuth';
import axios from 'utils/axios';

const PENDING_REFERRAL_KEY = 'pending_referral';

/**
 * Hook to track referral clicks from shared links.
 *
 * When a user clicks a link with ?ref= parameter:
 * - If logged in: tracks immediately via API
 * - If not logged in: saves to localStorage, tracks after login
 *
 * Usage in detail pages:
 * useReferralTracking('veille', veilleId);
 * useReferralTracking('formation', formationId);
 *
 * @param {string} itemType - Type of item (veille, formation, masterclass, etc.)
 * @param {string} itemId - ID of the item
 */
const useReferralTracking = (itemType, itemId) => {
  const location = useLocation();
  const { user, isLoggedIn } = useAuth();
  const trackedRef = useRef(false);

  useEffect(() => {
    // Prevent double tracking
    if (trackedRef.current || !itemType || !itemId) return;

    const params = new URLSearchParams(location.search);
    const ref = params.get('ref');
    const platform = params.get('platform') || 'unknown';

    if (!ref) {
      return;
    }

    const trackReferral = async () => {
      try {
        await axios.post('/api/gamification/track-referral/', {
          ref,
          item_type: itemType,
          item_id: itemId,
          platform
        });
        trackedRef.current = true;

        // Clean URL params after tracking (optional - removes ?ref= from URL)
        const newUrl = window.location.pathname;
        window.history.replaceState({}, '', newUrl);
      } catch (error) {
        trackedRef.current = true; // Prevent infinite retries
      }
    };

    if (isLoggedIn && user) {
      // User is logged in - track immediately
      trackReferral();
    } else {
      // User not logged in - save to localStorage for later
      // Note: For protected routes, AuthGuard saves this before redirect
      const pendingReferral = {
        ref,
        itemType,
        itemId,
        platform,
        timestamp: Date.now()
      };
      localStorage.setItem(PENDING_REFERRAL_KEY, JSON.stringify(pendingReferral));
      trackedRef.current = true;
    }
  }, [location.search, itemType, itemId, isLoggedIn, user]);
};

/**
 * Process pending referral after login.
 * Call this in JWTContext after successful login.
 *
 * @returns {Promise<void>}
 */
export const processPendingReferral = async () => {
  try {
    const pendingReferralStr = localStorage.getItem(PENDING_REFERRAL_KEY);
    if (!pendingReferralStr) {
      return;
    }
    const pendingReferral = JSON.parse(pendingReferralStr);

    // Check if referral is not too old (max 24 hours)
    const maxAge = 24 * 60 * 60 * 1000; // 24 hours in ms
    if (Date.now() - pendingReferral.timestamp > maxAge) {
      localStorage.removeItem(PENDING_REFERRAL_KEY);
      return;
    }

    const { ref, itemType, itemId, platform } = pendingReferral;

    await axios.post('/api/gamification/track-referral/', {
      ref,
      item_type: itemType,
      item_id: itemId,
      platform
    });

    // Remove from localStorage after successful tracking
    localStorage.removeItem(PENDING_REFERRAL_KEY);
  } catch (error) {
    // On error, remove from localStorage to prevent repeated failures
    localStorage.removeItem(PENDING_REFERRAL_KEY);
  }
};

export default useReferralTracking;
